import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Filter, Download, 
  ExternalLink, Mail, Calendar, Heart, 
  MoreVertical, ArrowUpDown 
} from 'lucide-react';

const Donors = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [groupedDonors, setGroupedDonors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDonors = async () => {
      try {
        // const response = await fetch("http://localhost:1415/api/donations/all");
        const token = localStorage.getItem("sevaa_admin_token");
// const response = await fetch("http://localhost/seva-backend/api/donations/all.php", {
const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/donations/all.php", {
    headers: { "Authorization": `Bearer ${token}` }
});
        const data = await response.json();
        
        if (data.success) {
          // Grouping logic by Email
          const donorsMap = new Map();
          
          data.donations.filter((d: any) => d.payment_status === 'success').forEach((d: any) => {
            if (!donorsMap.has(d.email)) {
              donorsMap.set(d.email, {
                id: d.id,
                name: d.name,
                email: d.email,
                totalDonated: 0,
                donationCount: 0,
                lastDonation: Number(d.amount),
                campaign: d.campaign_name || "General",
                date: d.created_at
              });
            }
            // Update totals
            const donor = donorsMap.get(d.email);
            donor.totalDonated += Number(d.amount);
            donor.donationCount += 1;
          });

          // Convert Map to Array
          const finalDonors = Array.from(donorsMap.values());
          setGroupedDonors(finalDonors);
        }
      } catch (error) {
        console.error("Failed to fetch donors", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDonors();
  }, []);

  // Search Filter
  const filteredDonors = groupedDonors.filter(d => 
    d.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    d.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* 1. Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight flex items-center gap-3">
            <Users className="text-orange-500" size={32} /> Donor Directory
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage and appreciate your NGO's supporters.</p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-all shadow-sm">
            <Download size={18} /> Export
          </button>
        </div>
      </div>

      {/* 2. Search and Filter Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input 
            type="text" 
            placeholder="Search by name or email..."
            className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500/20 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* 3. Donors Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-5">Donor Info</th>
                <th className="px-6 py-5">Total Contributions</th>
                <th className="px-6 py-5">Last Activity</th>
                <th className="px-6 py-5">Preferred Campaign</th>
                <th className="px-6 py-5">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? <tr><td colSpan={5} className="text-center py-6">Loading donors...</td></tr> : 
               filteredDonors.length === 0 ? <tr><td colSpan={5} className="text-center py-6">No donors found.</td></tr> :
               filteredDonors.map((donor) => (
                <tr key={donor.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-black text-sm uppercase">
                        {donor.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">{donor.name}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Mail size={12} /> {donor.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-black text-slate-900">₹{donor.totalDonated.toLocaleString('en-IN')}</p>
                    <p className="text-[10px] text-green-500 font-bold uppercase">{donor.donationCount} Donations</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-semibold text-slate-700">₹{donor.lastDonation.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-slate-400">{formatDate(donor.date)}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-xs font-bold bg-slate-100 px-3 py-1 rounded-full text-slate-600 truncate max-w-[150px] inline-block">
                      {donor.campaign}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ${
                      donor.donationCount > 1 ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {donor.donationCount > 1 ? 'Recurring' : 'One-time'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Donors;